import React, { useEffect } from 'react';
import { useSpring } from 'react-spring';
import { useDrag } from '@use-gesture/react';
import { logger } from '../../../utils/logger';
import { DashboardSection } from '../mobile/MobileDashboardColumnSwitcher';

interface UseDashboardColumnSwipeProps {
  /**
   * Ordered array of section IDs to navigate between
   */
  sectionIds: DashboardSection[];

  /**
   * Current active section ID
   */
  activeSection: DashboardSection;

  /**
   * Callback to switch to a new section
   */
  onSectionChange: (sectionId: DashboardSection) => void;

  /**
   * Callback to navigate back to topics list (called when swiping right on first section)
   */
  onBackToTopics: () => void;

  /**
   * Whether to enable the swipe gesture
   * @default true
   */
  enabled?: boolean;

  /**
   * Distance threshold as fraction of screen width to trigger navigation
   * @default 0.15
   */
  threshold?: number;
}

export function useDashboardColumnSwipe({
  sectionIds,
  activeSection,
  onSectionChange,
  onBackToTopics,
  enabled = true,
  threshold = 0.15,
}: UseDashboardColumnSwipeProps) {
  // Using ref to track current section to avoid dependency issues
  const currentSectionRef = React.useRef(activeSection);

  // Update ref whenever the activeSection changes
  useEffect(() => {
    currentSectionRef.current = activeSection;
  }, [activeSection]);

  // Create spring animation for content transform - moderate config
  const [{ x }, api] = useSpring(() => ({
    x: 0,
    config: { tension: 350, friction: 40 }, // Less stiff than before for smoother feel
  }));

  // Reset animation when enabled state changes
  useEffect(() => {
    if (!enabled) {
      api.start({ x: 0 });
    }
  }, [enabled, api]);

  // Memoize these values to avoid recalculation on every render
  const { currentIndex, isFirstSection, isLastSection } = React.useMemo(() => {
    const idx = sectionIds.indexOf(activeSection);
    return {
      currentIndex: idx,
      isFirstSection: idx === 0,
      isLastSection: idx === sectionIds.length - 1,
    };
  }, [sectionIds, activeSection]);

  // Set up drag gesture handler
  const bind = useDrag(
    ({ movement: [mx], last, down, velocity: [vx], cancel }) => {
      // Skip if disabled
      if (!enabled) {
        cancel?.();
        return;
      }

      // Calculate screen width for thresholds
      const screenWidth = window.innerWidth;
      const thresholdDistance = screenWidth * threshold;

      // Determine swipe direction
      const isRightSwipe = mx > 0;
      const isLeftSwipe = mx < 0;

      // Exit early if swiping in an invalid direction
      // - Can't swipe left (forward) on last section
      // - Can navigate anywhere with right swipe
      if (isLeftSwipe && isLastSection) {
        cancel?.();
        return;
      }

      // Apply spring animation during drag - more visible now
      if (down) {
        // Resistance factor - lower means less resistance to dragging
        const resistance = 3; // Reduced resistance for more visible movement

        // Limit the maximum distance for moderate movement
        const maxDistance = screenWidth * 0.25; // Increased to 25% of screen width

        // Apply resistance to make it harder to drag past limits
        let movement = mx;

        // Apply moderate resistance for more visible effect
        movement = movement / resistance;

        // Apply maximum distance constraint
        if (movement > maxDistance) movement = maxDistance;
        if (movement < -maxDistance) movement = -maxDistance;

        // Additional resistance at boundaries (but less than before)
        if ((isFirstSection && isRightSwipe) || (isLastSection && isLeftSwipe)) {
          movement = movement / 1.5; // Less resistance at boundaries
        }

        // Update position with spring - using immediate: false for smoother feel
        api.start({ x: movement, immediate: false });
      }

      // When the drag is released
      if (last) {
        const isOverThreshold = Math.abs(mx) > thresholdDistance;
        const isHighVelocity = Math.abs(vx) > 0.3;
        const shouldNavigate = isOverThreshold || isHighVelocity;

        if (shouldNavigate) {
          if (isRightSwipe) {
            // Right swipe - go back
            if (isFirstSection) {
              // If on first section, navigate back to projects drawer
              logger.component('Dashboard swipe: First section, opening projects drawer');

              // For back navigation to drawer, ensure everything is reset first
              // Force a complete animation reset before opening the drawer
              api.start({
                x: 0,
                immediate: true,
                onRest: () => {
                  // Only after we're sure position is reset, open drawer
                  onBackToTopics();
                },
              });
            } else {
              // Navigate to previous section
              const prevSectionId = sectionIds[currentIndex - 1];
              logger.component('Dashboard swipe: Moving to previous section', {
                from: activeSection,
                to: prevSectionId,
              });

              // Ensure proper reset before navigation
              api.start({
                x: 0,
                immediate: true,
                onRest: () => {
                  // Navigate only after position is fully reset
                  onSectionChange(prevSectionId);
                },
              });
            }
          } else if (isLeftSwipe && !isLastSection) {
            // Left swipe - go forward to next section
            const nextSectionId = sectionIds[currentIndex + 1];
            logger.component('Dashboard swipe: Moving to next section', {
              from: activeSection,
              to: nextSectionId,
            });

            // Ensure proper reset before navigation
            api.start({
              x: 0,
              immediate: true,
              onRest: () => {
                // Navigate only after position is fully reset
                onSectionChange(nextSectionId);
              },
            });
          }
        } else {
          // Not enough distance/velocity to navigate, reset with a spring animation
          // Using a moderate spring for natural reset
          api.start({
            x: 0,
            config: { tension: 400, friction: 40 },
            immediate: false,
          });
        }
      }
    },
    {
      // Apply drag only to horizontal movements
      axis: 'x',
      // Use touch events for mobile with passive: true to avoid the warning
      pointer: { touch: true, capture: false, passive: true },
      // Filter out small movements
      filterTaps: true,
      // No delay to make it feel responsive
      delay: 0,
    }
  );

  return {
    bind,
    x,
  };
}
